import { defineStore } from "pinia";
import { cloneDeep } from "lodash";
import { getRequestCase, handlePublicApiError } from "./backendApi";
import { getRequest } from "@/services/backendApi";
import { useSnackbarStore } from "../snackbarStore";
import { decryptID } from "@/utils/security";

import * as caseDataMeta from "./8_caseDataMeta";
import * as clientsMeta from "./1_clientsMeta";
import * as vehiclesMeta from "./2_vehiclesMeta";
import * as opponentsMeta from "./3_opponentsMeta";
import * as witnessesMeta from "./4_witnessesMeta";
import * as accidentMeta from "./5_accidentMeta";
import * as policeProsecutionMeta from "./6_policeProsecutionMeta";
import { currentPath, isQuestionnaireRoutePath, withQuestionnaireView } from "./questionnaireSource";

type State = {
  savingVehicles: boolean;
  /**
   * Fragebogen-Sicht (Kundenfeedback 25.10.2026, VIII): geladen mit
   * ?view=fragebogen, Speicherungen gehen mit ?source=fragebogen
   * (siehe questionnaireSource.ts).
   */
  questionnaireView: boolean;

  caseData: Record<string, any>;
  clients: Record<string, any>[];
  vehicles: Record<string, any>[];
  opponents: Record<string, any>[];
  witnesses: Record<string, any>[];
  accident: Record<string, any>;
  policeProsecution: Record<string, any>;
  idPreview: number | null;
  previewValues: Record<string, any>;
  kanzleiInfo: { name: string; logoUrl: string | null };

  publicToken: string | null;
  publicSession: { status: string; kind: string; expiresAt: string; submittedAt?: string | null } | null;
  publicError: number | null; // HTTP-Code des letzten fatalen Public-Fehlers (404/409/410)

  lastSavedCaseData: Record<string, any>;
  lastSavedClients: Record<string, any>[];
  lastSavedVehicles: Record<string, any>[];
  lastSavedOpponents: Record<string, any>[];
  lastSavedWitnesses: Record<string, any>[];
  lastSavedAccident: Record<string, any>;
  lastSavedPoliceProsecution: Record<string, any>;
};

function getDefaultState() {
  return {
    savingVehicles: false,
    questionnaireView: false,

    caseData: { ...caseDataMeta.defaultCaseData },
    clients: [{ ...clientsMeta.defaultClient }],
    vehicles: [{ ...vehiclesMeta.defaultVehicle }],
    opponents: [{ ...opponentsMeta.defaultOpponent }],
    witnesses: [{ ...witnessesMeta.defaultWitness }],
    accident: { ...accidentMeta.defaultAccident },
    policeProsecution: { ...policeProsecutionMeta.defaultPoliceProsecution },
    idPreview: null,
    previewValues: {},
    kanzleiInfo: { name: '', logoUrl: null },

    publicToken: null,
    publicSession: null,
    publicError: null,

    lastSavedCaseData: { ...caseDataMeta.defaultCaseData },
    lastSavedClients: [{ ...clientsMeta.defaultClient }],
    lastSavedVehicles: [{ ...vehiclesMeta.defaultVehicle }],
    lastSavedOpponents: [{ ...opponentsMeta.defaultOpponent }],
    lastSavedWitnesses: [{ ...witnessesMeta.defaultWitness }],
    lastSavedAccident: { ...accidentMeta.defaultAccident },
    lastSavedPoliceProsecution: { ...policeProsecutionMeta.defaultPoliceProsecution },
  };
}

export const useCaseStore = defineStore("case", {
  state: (): State => getDefaultState(),

  actions: {
    /**
     * `view: "fragebogen"` lädt die Fragebogen-Sicht (Kundenfeedback
     * 25.10.2026, VIII); auf der Fragebogen-Seite (/fragebogen/:id) immer.
     * Bereiche der Akte laden ohne – dort zählen die Aktenwerte.
     */
    async loadCase(idCase: string, options: { view?: "fragebogen" } = {}) {
      const snackbar = useSnackbarStore();
      this.resetCase();
      this.questionnaireView = options.view === "fragebogen" || isQuestionnaireRoutePath(currentPath());

      // Die Fragebogen-/Zusammenfassungs-Routen werden teils mit verschlüsselter
      // (z.B. Router-Redirect für Light-User, Dashboard-Banner) und teils mit
      // Klartext-ID (RoleBasedCTA) aufgerufen. Das Backend (`/questionnaire`)
      // erwartet jedoch eine Klartext-Integer-ID (strconv.Atoi). Wird eine
      // verschlüsselte ID ungeprüft durchgereicht, parst das Backend sie als
      // falsche Fall-ID → 403 (keine Berechtigung) → keine Daten + fehlender
      // openedAt → "Fehlender Zeitstempel" bei jedem Autosave. Daher hier
      // zentral normalisieren: verschlüsselte ID entschlüsseln, sonst Klartext.
      const normalizedId = decryptID(idCase) ?? idCase;

      try {
        const response = await getRequestCase(withQuestionnaireView(`/protected/cases/${normalizedId}/questionnaire`, this.questionnaireView));

        const caseData = caseDataMeta.prepareCaseDataForLoad(response.caseData);
        const clients = (Array.isArray(response.clients) ? response.clients : []).map(clientsMeta.prepareClientForLoad);
        const vehicles = (Array.isArray(response.vehicles) ? response.vehicles : []).map(vehiclesMeta.prepareVehicleForLoad);
        const opponents = (Array.isArray(response.opponents) ? response.opponents : []).map(opponentsMeta.prepareOpponentForLoad);
        const witnesses = (Array.isArray(response.witnesses) ? response.witnesses : []).map(witnessesMeta.prepareWitnessForLoad);
        const accident = response.accident ? accidentMeta.prepareAccidentForLoad(response.accident) : { ...accidentMeta.defaultAccident };
        const policeProsecution = response.policeProsecution ? policeProsecutionMeta.preparePoliceProsecutionForLoad(response.policeProsecution) : { ...policeProsecutionMeta.defaultPoliceProsecution };
        const idPreview = response.preview?.idPreview ?? null;
        const previewValues = response.preview?.values ?? {};
        const kanzleiInfo = {
          name: response.kanzlei?.name ?? '',
          logoUrl: response.kanzlei?.logoUrl ?? null,
        };

        this.$patch({
          caseData,
          clients,
          vehicles,
          opponents,
          witnesses,
          accident,
          policeProsecution,
          idPreview,
          previewValues,
          kanzleiInfo,

          lastSavedCaseData: cloneDeep(caseData),
          lastSavedClients: cloneDeep(clients),
          lastSavedVehicles: cloneDeep(vehicles),
          lastSavedOpponents: cloneDeep(opponents),
          lastSavedWitnesses: cloneDeep(witnesses),
          lastSavedAccident: cloneDeep(accident),
          lastSavedPoliceProsecution: cloneDeep(policeProsecution),
        });
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : (error as any)?.message;
        if (msg) {
          snackbar.showSnackbar({ message: msg, type: "error" });
        }
      }
    },

    /**
     * Lädt den Fragebogen über den öffentlichen Token-Endpunkt (Vor-Ort-Modus).
     * publicToken wird VOR dem Laden gesetzt, damit alle Save-Funktionen der
     * Metas über backendApi.ts auf die Token-URLs umgeschrieben werden.
     */
    async loadPublicSession(token: string): Promise<boolean> {
      this.resetCase();
      this.publicToken = token;
      try {
        const response = await getRequest(
          `/questionnaire-session?token=${encodeURIComponent(token)}`,
          handlePublicApiError,
          true
        );
        const caseData = caseDataMeta.prepareCaseDataForLoad(response.caseData);
        const clients = (Array.isArray(response.clients) ? response.clients : []).map(clientsMeta.prepareClientForLoad);
        const vehicles = (Array.isArray(response.vehicles) ? response.vehicles : []).map(vehiclesMeta.prepareVehicleForLoad);
        const opponents = (Array.isArray(response.opponents) ? response.opponents : []).map(opponentsMeta.prepareOpponentForLoad);
        const witnesses = (Array.isArray(response.witnesses) ? response.witnesses : []).map(witnessesMeta.prepareWitnessForLoad);
        const accident = response.accident ? accidentMeta.prepareAccidentForLoad(response.accident) : { ...accidentMeta.defaultAccident };
        const policeProsecution = response.policeProsecution ? policeProsecutionMeta.preparePoliceProsecutionForLoad(response.policeProsecution) : { ...policeProsecutionMeta.defaultPoliceProsecution };
        this.$patch({
          caseData,
          clients,
          vehicles,
          opponents,
          witnesses,
          accident,
          policeProsecution,
          kanzleiInfo: { name: response.kanzlei?.name ?? "", logoUrl: response.kanzlei?.logoUrl ?? null },
          publicSession: response.session ?? null,
          lastSavedCaseData: cloneDeep(caseData),
          lastSavedClients: cloneDeep(clients),
          lastSavedVehicles: cloneDeep(vehicles),
          lastSavedOpponents: cloneDeep(opponents),
          lastSavedWitnesses: cloneDeep(witnesses),
          lastSavedAccident: cloneDeep(accident),
          lastSavedPoliceProsecution: cloneDeep(policeProsecution),
        });
        return true;
      } catch (error: any) {
        this.publicError = error?.statusCode ?? 500;
        return false;
      }
    },

    /** `clientSource: "akte"`: Aufruf aus der Akte (XXIV) – Kundendaten ohne Fragebogen-Version. */
    async saveAll(options: { clientSource?: clientsMeta.ClientEditSource } = {}): Promise<void> {
      await caseDataMeta.saveRemarks();
      await clientsMeta.saveClients({ source: options.clientSource });
      await vehiclesMeta.saveVehicles();
      await opponentsMeta.saveOpponents();
      await witnessesMeta.saveWitnessesMaster();
      await witnessesMeta.saveWitnesses();
      await accidentMeta.saveAccident();
      await policeProsecutionMeta.savePoliceProsecution();
    },

    resetCase() {
      this.$reset();
    },
  },
});
